import * as i0 from '@angular/core';
import { EventEmitter, PLATFORM_ID, Component, ChangeDetectionStrategy, Inject, ViewChild, Input, Output, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { isPlatformBrowser, CommonModule } from '@angular/common';

/* name of class with animation fade out */
const _c0 = ["typedText"];
const _c1 = ["cursorRef"];
function NgxTypedWriterComponent_span_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 3, 1);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1("", ctx_r0.cursorChar, " ");
  }
}
const FADE_OUT_CLASS = 'typed-fade-out';

/**
 * The function `typeHtmlChars` is used to skip over HTML characters in a string if the `isHTML` flag
 * is set to true.
 * @param {boolean} isHTML - A boolean value indicating whether the current string contains HTML
 * characters.
 * @param {string} currentString - The current string that needs to be processed. It can contain HTML
 * characters that need to be skipped over.
 * @param {number} currentStringPosition - The current position in the string that is being processed.
 * @returns the updated value of `currentStringPosition`.
 */
function typeHtmlChars(isHTML, currentString, currentStringPosition) {
  if (!isHTML) return currentStringPosition;
  const currentCharacter = currentString.substring(currentStringPosition).charAt(0);
  if (currentCharacter === '<' || currentCharacter === '&') {
    let endTag = '';
    if (currentCharacter === '<') {
      endTag = '>';
    } else {
      endTag = ';';
    }
    while (currentString.substring(currentStringPosition + 1).charAt(0) !== endTag) {
      currentStringPosition++;
      if (currentStringPosition + 1 > currentString.length) {
        break;
      }
    }
    currentStringPosition++;
  }
  return currentStringPosition;
}
/**
 * The function `backSpaceHtmlChars` is a function that takes in a boolean `isHTML`, a
 * string `currentString`, and a number `currentStringPosition`, and returns a new number representing
 * the updated `currentStringPosition` after removing HTML characters.
 * @param {boolean} isHTML - A boolean value indicating whether the current string contains HTML
 * characters or not.
 * @param {string} currentString - The `currentString` parameter is a string that represents the
 * current text or HTML content.
 * @param {number} currentStringPosition - The current position within the current string.
 * @returns the updated value of `currentStringPosition`.
 */
function backSpaceHtmlChars(isHTML, currentString, currentStringPosition) {
  if (!isHTML) return currentStringPosition;
  const currentCharacter = currentString.substring(currentStringPosition).charAt(0);
  if (currentCharacter === '>' || currentCharacter === ';') {
    let endTag = '';
    if (currentCharacter === '>') {
      endTag = '<';
    } else {
      endTag = '&';
    }
    while (currentString.substring(currentStringPosition - 1).charAt(0) !== endTag) {
      currentStringPosition--;
      if (currentStringPosition < 0) {
        break;
      }
    }
    currentStringPosition--;
  }
  return currentStringPosition;
}
/**
 * The function `shuffleStringsIfNeeded` shuffles an array of strings if a boolean flag is set to true,
 * otherwise it returns the original array.
 * @param {boolean} shuffle - A boolean value indicating whether the strings should be shuffled or not.
 * @param {string[]} strings - An array of strings that need to be shuffled if the `shuffle` parameter
 * is set to `true`.
 * @returns an array of strings. If the `shuffle` parameter is `false`, it returns the original
 * `strings` array. If `shuffle` is `true`, it returns a shuffled version of the `strings` array.
 */
function shuffleStringsIfNeeded(shuffle, strings) {
  if (!shuffle) return strings;
  return strings.sort(() => Math.random() - 0.5);
}
class NgxTypedWriterComponent {
  constructor(platformId, renderer2) {
    this.platformId = platformId;
    this.renderer2 = renderer2;
    this.strings = [];
    this.typeSpeed = 40;
    this.startDelay = 0;
    this.backSpeed = 40;
    this.smartBackspace = false;
    this.shuffle = false;
    this.backDelay = 1000;
    this.isHTML = false;
    this.fadeOut = false;
    this.loop = true;
    this.showCursor = true;
    this.cursorChar = '|';
    this.fadeOutDelay = 500;
    this.currentStringIndex = 0;
    this.currentString = '';
    this.currentStringPosition = 0;
    this.isTypingPaused = false;
    this.stopNum = 0;
    this.destroy = new EventEmitter();
    this.initTyped = new EventEmitter();
    this.completeLoop = new EventEmitter();
  }
  ngOnInit() {
    isPlatformBrowser(this.platformId) && this.init();
  }
  ngOnDestroy() {
    clearTimeout(this.timeout);
    this.destroy.emit();
  }
  init() {
    this.strings = shuffleStringsIfNeeded(this.shuffle, this.strings);
    this.currentString = this.strings[this.currentStringIndex];
    this.timeout = setTimeout(() => {
      this.typeString();
      this.initTyped.emit();
    }, this.startDelay);
  }
  typeString() {
    if (this.isTypingPaused) return;
    if (this.fadeOut) {
      const typedElement = this.typedTextRef.nativeElement;
      this.renderer2.removeClass(typedElement, FADE_OUT_CLASS);
      if (this.showCursor) {
        const cursorElement = this.cursor?.nativeElement;
        this.renderer2.removeClass(cursorElement, FADE_OUT_CLASS);
      }
    }
    if (this.currentStringPosition < this.currentString.length) {
      this.typeCharacter();
    } else {
      this.isTypingPaused = true;
      this.timeout = setTimeout(() => {
        this.isTypingPaused = false;
        this.timeout = setTimeout(() => {
          this.backspaceString();
        }, this.backDelay);
      }, this.typeSpeed);
    }
  }
  typeCharacter() {
    this.timeout = setTimeout(() => {
      this.currentStringPosition = typeHtmlChars(this.isHTML, this.currentString, this.currentStringPosition);
      const nextString = this.currentString.substring(0, this.currentStringPosition + 1);
      const lastItem = this.strings.at(-1);
      this.typedTextRef.nativeElement.innerHTML = nextString;
      this.currentStringPosition++;
      if (nextString === lastItem && !this.loop) {
        this.completeLoop.emit();
        return;
      }
      this.typeString();
    }, this.typeSpeed);
  }
  backspaceString() {
    if (this.isTypingPaused) return;
    if (this.fadeOut) {
      this.initFadeOut();
      return;
    }
    if (this.currentStringPosition > this.stopNum) {
      this.backspaceCharacter();
    } else {
      this.isTypingPaused = true;
      this.timeout = setTimeout(() => {
        this.isTypingPaused = false;
        this.currentStringIndex++;
        if (this.currentStringIndex >= this.strings.length) {
          if (this.loop) {
            this.currentStringIndex = 0;
            // this.typeString();
          } else {
            return; // Finished typing all strings
          }
        }
        this.currentString = this.strings[this.currentStringIndex];
        this.timeout = setTimeout(() => {
          this.typeString();
        }, this.typeSpeed);
      }, this.typeSpeed);
    }
  }
  backspaceCharacter() {
    const currentString = this.typedTextRef.nativeElement.innerHTML;
    this.currentStringPosition = backSpaceHtmlChars(this.isHTML, this.currentString, this.currentStringPosition);
    const curStringAtPosition = currentString.substring(0, this.currentStringPosition);
    this.typedTextRef.nativeElement.innerHTML = curStringAtPosition;
    this.timeout = setTimeout(() => {
      //  if smartBack is enabled
      if (this.smartBackspace) {
        // the remaining part of the current string is equal of the same part of the new string
        const nextStringPartial = this.strings[this.currentStringIndex + 1];
        const compare = curStringAtPosition === nextStringPartial?.substring(0, this.currentStringPosition);
        if (nextStringPartial && compare) {
          this.stopNum = this.currentStringPosition - 1;
        } else {
          this.stopNum = 0;
        }
      }
      if (this.currentStringPosition > this.stopNum) {
        // subtract characters one by one
        this.currentStringPosition--;
        // loop the function
        this.backspaceString();
      } else if (this.currentStringPosition <= this.stopNum) {
        // if the stop number has been reached, increase
        // array position to next string
        this.currentStringIndex++;
        // When looping, begin at the beginning after backspace complete
        if (this.currentStringIndex === this.strings.length) {
          this.currentStringIndex = 0;
        }
        this.typeString();
      }
    }, this.backSpeed);
  }
  initFadeOut() {
    const typedElement = this.typedTextRef.nativeElement;
    this.renderer2.addClass(typedElement, FADE_OUT_CLASS);
    if (this.showCursor) {
      const cursorElement = this.cursor.nativeElement;
      this.renderer2.addClass(cursorElement, FADE_OUT_CLASS);
    }
    this.timeout = setTimeout(() => {
      this.currentStringIndex++;
      typedElement.innerHTML = '';
      // Resets current string if end of loop reached
      if (this.strings.length > this.currentStringIndex) {
        this.currentStringPosition = 0;
        this.currentString = this.strings[this.currentStringIndex];
        this.typeString();
      } else {
        this.currentStringPosition = 0;
        this.currentStringIndex = 0;
        this.currentString = this.strings[this.currentStringIndex];
        this.typeString(); // this.currentStringIndex++;
      }
    }, this.fadeOutDelay);
  }
  static {
    this.ɵfac = function NgxTypedWriterComponent_Factory(t) {
      return new (t || NgxTypedWriterComponent)(i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.Renderer2));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NgxTypedWriterComponent,
      selectors: [["ngx-typed-writer"]],
      viewQuery: function NgxTypedWriterComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 7);
          i0.ɵɵviewQuery(_c1, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.typedTextRef = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.cursor = _t.first);
        }
      },
      inputs: {
        strings: "strings",
        typeSpeed: "typeSpeed",
        startDelay: "startDelay",
        backSpeed: "backSpeed",
        smartBackspace: "smartBackspace",
        shuffle: "shuffle",
        backDelay: "backDelay",
        isHTML: "isHTML",
        fadeOut: "fadeOut",
        loop: "loop",
        showCursor: "showCursor",
        cursorChar: "cursorChar",
        fadeOutDelay: "fadeOutDelay"
      },
      outputs: {
        destroy: "destroy",
        initTyped: "initTyped",
        completeLoop: "completeLoop"
      },
      decls: 3,
      vars: 1,
      consts: [["typedText", ""], ["cursorRef", ""], ["class", "typing-cursor", 4, "ngIf"], [1, "typing-cursor"]],
      template: function NgxTypedWriterComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelement(0, "span", null, 0);
          i0.ɵɵtemplate(2, NgxTypedWriterComponent_span_2_Template, 3, 1, "span", 2);
        }
        if (rf & 2) {
          i0.ɵɵadvance(2);
          i0.ɵɵproperty("ngIf", ctx.showCursor);
        }
      },
      dependencies: [i1.NgIf],
      styles: [".typing-cursor[_ngcontent-%COMP%]{-webkit-animation:_ngcontent-%COMP%_blink .7s infinite;display:inline-block;opacity:1;animation:_ngcontent-%COMP%_blink .7s infinite}@keyframes _ngcontent-%COMP%_blink{0%{opacity:1}50%{opacity:0}to{opacity:1}}.typed-fade-out[_ngcontent-%COMP%]{opacity:0;transition:opacity .25s}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxTypedWriterComponent, [{
    type: Component,
    args: [{
      selector: 'ngx-typed-writer',
      changeDetection: ChangeDetectionStrategy.OnPush,
      template: `
    <span #typedText> </span>
    <span #cursorRef class="typing-cursor" *ngIf="showCursor"
      >{{ cursorChar }}
    </span>
  `,
      styles: [".typing-cursor{-webkit-animation:blink .7s infinite;display:inline-block;opacity:1;animation:blink .7s infinite}@keyframes blink{0%{opacity:1}50%{opacity:0}to{opacity:1}}.typed-fade-out{opacity:0;transition:opacity .25s}\n"]
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [PLATFORM_ID]
    }]
  }, {
    type: i0.Renderer2
  }], {
    typedTextRef: [{
      type: ViewChild,
      args: ['typedText', {
        static: true
      }]
    }],
    cursor: [{
      type: ViewChild,
      args: ['cursorRef']
    }],
    strings: [{
      type: Input
    }],
    typeSpeed: [{
      type: Input
    }],
    startDelay: [{
      type: Input
    }],
    backSpeed: [{
      type: Input
    }],
    smartBackspace: [{
      type: Input
    }],
    shuffle: [{
      type: Input
    }],
    backDelay: [{
      type: Input
    }],
    isHTML: [{
      type: Input
    }],
    fadeOut: [{
      type: Input
    }],
    loop: [{
      type: Input
    }],
    showCursor: [{
      type: Input
    }],
    cursorChar: [{
      type: Input
    }],
    fadeOutDelay: [{
      type: Input
    }],
    destroy: [{
      type: Output
    }],
    initTyped: [{
      type: Output
    }],
    completeLoop: [{
      type: Output
    }]
  });
})();
class NgxTypedWriterModule {
  static {
    this.ɵfac = function NgxTypedWriterModule_Factory(t) {
      return new (t || NgxTypedWriterModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NgxTypedWriterModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxTypedWriterModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule],
      declarations: [NgxTypedWriterComponent],
      exports: [NgxTypedWriterComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgxTypedWriterComponent, NgxTypedWriterModule };
